import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  Snackbar,
  Alert,
  Grid,
  InputAdornment,
  IconButton,
  Collapse,
  Tooltip,
  Paper,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  KeyboardArrowDown,
  ExpandMore,
  AccountBalanceWallet,
  SwapHoriz,
  Receipt,
  CheckCircleOutline,
  HourglassEmpty,
  PendingActions,
  EventAvailable,
  ShoppingCart,
  Tune,
  Close,
  CalendarMonth,
  Edit,
  DeleteOutline,
  Payments,
} from '@mui/icons-material';
import api from '../../../services/api';
import { withdrawalRequestService } from '../services';

type PayoutStatus = 'no_sales' | 'not_paid' | 'partial' | 'complete' | 'pending';

interface EventPayout {
  _id: string;
  title: string;
  startDate: string;
  endDate: string;
  feePercentage: number;
  parish: {
    _id: string;
    name: string;
    city: string;
    state: string;
  };
  payoutInfo: {
    totalAmountPaid: number;
    totalPlatformFee: number;
    totalParishFee: number;
    totalRegistrations: number;
    feePercentage?: number;
    shouldPayout: boolean;
    payoutAmount: number;
    paymentMethodsStats?: {
      pix: { count: number; totalAmount: number; totalProfit: number };
      creditCard: { count: number; totalAmount: number; totalProfit: number };
      debitCard: { count: number; totalAmount: number; totalProfit: number };
      other: { count: number; totalAmount: number; totalProfit: number };
    };
    ticketBreakdown?: Array<{
      _id: string;
      count: number;
      totalAmount: number;
      totalParishFee: number;
      totalPlatformFee: number;
    }>;
    withdrawalSummary?: {
      totalParishFee?: number;
      alreadyWithdrawn: number;
      availableBalance: number;
      pendingWithdrawalAmount: number;
      payoutStatus?: PayoutStatus;
      settlementChannels?: {
        pagarmeParishNet: number;
        mercadopagoCardParishNet: number;
        mercadopagoPixParishNet: number;
        mercadopagoAmbiguousParishNet: number;
      };
      pagarmeRecipientBalanceReais: number | null;
      pagarmeRecipientBalanceCode?: 'no_recipient' | 'fetch_failed';
      mercadopagoCardAvailableReais: number;
      reconciliationOk?: boolean;
      reconciliationMessages?: string[];
    };
    withdrawals?: Array<{
      id: string;
      amount: number;
      status: string;
      source?: 'parish_request' | 'admin_manual';
      requestedAt: string;
      completedAt?: string;
      processingAt?: string;
      rejectionReason?: string;
      payoutBreakdown?: {
        pagarmeAmountReais: number;
        mercadopagoAmountReais: number;
      };
    }>;
  };
}

const WITHDRAWAL_STATUS: Record<
  string,
  { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }
> = {
  pending: { label: 'Pendente', color: 'warning' },
  processing: { label: 'Em processamento', color: 'info' },
  completed: { label: 'Concluído', color: 'success' },
  rejected: { label: 'Rejeitado', color: 'error' },
  cancelled: { label: 'Cancelado', color: 'default' },
};

const formatPagarmeBalance = (
  reais: number | null | undefined,
  code?: 'no_recipient' | 'fetch_failed'
) => {
  if (reais != null) return formatCurrency(reais);
  if (code === 'no_recipient') return 'Sem recebedor';
  if (code === 'fetch_failed') return 'Erro ao consultar';
  return '—';
};

const PAYOUT_STATUS: Record<
  PayoutStatus,
  { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }
> = {
  no_sales: { label: 'Sem vendas', color: 'default' },
  not_paid: { label: 'Nada repassado', color: 'warning' },
  partial: { label: 'Parcial', color: 'info' },
  complete: { label: 'Quitado', color: 'success' },
  pending: { label: 'Saque em andamento', color: 'info' },
};

interface PayoutFilters {
  dateFilter?: 'active' | 'past' | 'upcoming' | 'all';
  startDate?: string;
  endDate?: string;
  search?: string;
  parishId?: string;
  withSales?: 'all' | 'withSales' | 'withoutSales';
  minAmount?: string;
  status?: string;
  payoutStatus?: 'all' | PayoutStatus;
}

interface PayoutDashboardSummary {
  eventCount: number;
  eventsWithSalesCount: number;
  totalGross: number;
  totalLiquid: number;
  totalWithdrawn: number;
  totalRemaining: number;
  totalPlatformFee: number;
  totalPendingAmount: number;
  totalRegistrations: number;
  countComplete: number;
  countNotPaid: number;
  countPartial: number;
  countPending: number;
  countNoSales: number;
  /** Líquido Mercado Pago ainda não atribuído a saques. */
  totalMercadopagoRemaining?: number;
  /** Líquido paróquia nas vendas via Pagar.me (total vendido). */
  totalPagarmeParishNet?: number;
  /** Líquido Pagar.me ainda não atribuído a saques (contábil). */
  totalPagarmeRemaining?: number;
  /** Saldo disponível na carteira Pagar.me (API do recebedor). */
  totalPagarmeWalletAvailable?: number | null;
  totalPagarmeWaitingFunds?: number;
  totalPagarmeWalletTransferred?: number;
  /** min(contábil, caixa) — o que dá para transferir agora. */
  totalPagarmeTransferable?: number | null;
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getQueryErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const response = (error as { response?: { data?: { message?: unknown } } }).response;
  const message = response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

const getLiquidParish = (event: EventPayout): number => {
  const payoutInfo = event.payoutInfo || ({} as EventPayout['payoutInfo']);
  const feePercentage = event.feePercentage || payoutInfo.feePercentage || 6.9;
  const totalAmountPaid = Number(payoutInfo.totalAmountPaid || 0);
  if (totalAmountPaid <= 0) return 0;

  const fromGross = parseFloat((totalAmountPaid * (1 - feePercentage / 100)).toFixed(2));
  const wsLiquid = payoutInfo.withdrawalSummary?.totalParishFee;
  if (wsLiquid != null && wsLiquid > 0) {
    const expectedPlatformCut = parseFloat((totalAmountPaid * (feePercentage / 100)).toFixed(2));
    if (Math.abs(wsLiquid - expectedPlatformCut) < 0.02) return fromGross;
    return wsLiquid;
  }

  const payoutAmount = Number(payoutInfo.payoutAmount || 0);
  const totalParishFee = Number(payoutInfo.totalParishFee || 0);
  if (totalParishFee <= 0) return payoutAmount > 0 ? payoutAmount : fromGross;

  const expectedPlatformCut = parseFloat((totalAmountPaid * (feePercentage / 100)).toFixed(2));
  if (Math.abs(totalParishFee - expectedPlatformCut) < 0.02) return fromGross;
  if (Math.abs(totalParishFee - fromGross) < 0.02) return totalParishFee;
  return fromGross;
};

const getAvailableBalance = (event: EventPayout): number => {
  const liquidParish = getLiquidParish(event);
  const alreadyWithdrawn = event.payoutInfo?.withdrawalSummary?.alreadyWithdrawn ?? 0;
  return Math.max(0, parseFloat((liquidParish - alreadyWithdrawn).toFixed(2)));
};

const getEventPayoutStatus = (event: EventPayout): PayoutStatus => {
  const ws = event.payoutInfo?.withdrawalSummary;
  if (ws?.payoutStatus) return ws.payoutStatus;

  const totalRegs = event.payoutInfo?.totalRegistrations ?? 0;
  const liquidParish = getLiquidParish(event);
  const alreadyWithdrawn = ws?.alreadyWithdrawn ?? 0;
  const availableBalance = getAvailableBalance(event);
  const pendingWithdrawalAmount = ws?.pendingWithdrawalAmount ?? 0;

  if (totalRegs === 0 || liquidParish <= 0) return 'no_sales';
  if (pendingWithdrawalAmount > 0) return 'pending';
  if (availableBalance <= 0.01 && alreadyWithdrawn > 0) return 'complete';
  if (alreadyWithdrawn <= 0.01) return 'not_paid';
  return 'partial';
};

interface SummaryStatCardProps {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  icon?: React.ReactNode;
}

const SummaryStatCard: React.FC<SummaryStatCardProps> = ({ label, value, sub, color, icon }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: color || 'text.primary', lineHeight: 1.3 }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary" display="block">
              {sub}
            </Typography>
          )}
        </Box>
        {icon && <Box sx={{ color: color || 'text.secondary', opacity: 0.85 }}>{icon}</Box>}
      </Stack>
    </CardContent>
  </Card>
);

const PAYOUT_ROW_GRID = '40px minmax(180px, 1fr) 132px 120px 132px';

const payoutHeaderCellSx: SxProps<Theme> = {
  py: 1.25,
  px: 2,
  bgcolor: (theme) =>
    theme.palette.mode === 'light' ? theme.palette.grey[100] : 'rgba(255,255,255,0.06)',
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'text.secondary',
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
};

const formatEventSubtitle = (
  parishName: string,
  startDate: string,
  endDate: string,
  formatDate: (d: string | Date | undefined) => string
): string => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  const period = start && end ? `${start} – ${end}` : start || end;
  if (parishName && parishName !== 'N/A' && period) return `${parishName} · ${period}`;
  if (parishName && parishName !== 'N/A') return parishName;
  return period || '';
};

interface PayoutExpandPanelProps {
  event: EventPayout;
  formatDate: (date: string | Date | undefined) => string;
  onWithdrawalRecorded?: () => void | Promise<void>;
  onNotify?: (message: string, severity: 'success' | 'error') => void;
}

const accordionSx = {
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  mb: 1,
  '&:before': { display: 'none' },
  boxShadow: 'none',
} as const;

interface FlowStepProps {
  label: string;
  value: string;
  sub?: string;
  emphasis?: 'hero' | 'deduct' | 'neutral';
}

const FlowStep: React.FC<FlowStepProps> = ({ label, value, sub, emphasis = 'neutral' }) => (
  <Box sx={{ flex: '1 1 0', minWidth: 0, textAlign: 'center', px: 0.5 }}>
    <Typography variant="caption" color="text.secondary" display="block" noWrap>
      {label}
    </Typography>
    <Typography
      variant={emphasis === 'hero' ? 'h5' : 'subtitle1'}
      fontWeight={emphasis === 'hero' ? 700 : 600}
      color={
        emphasis === 'hero'
          ? 'success.main'
          : emphasis === 'deduct'
            ? 'error.main'
            : 'text.primary'
      }
      sx={{ lineHeight: 1.3, mt: 0.25 }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary" display="block">
        {sub}
      </Typography>
    )}
  </Box>
);

const PayoutExpandPanel: React.FC<PayoutExpandPanelProps> = ({
  event,
  formatDate,
  onWithdrawalRecorded,
  onNotify,
}) => {
  const [manualAmount, setManualAmount] = useState('');
  const [recording, setRecording] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState<{ id: string; amount: number } | null>(null);
  const [correctionAmount, setCorrectionAmount] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; amount: number } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const feePercentage = event.feePercentage || 6.9;
  const totalAmountPaid = event.payoutInfo?.totalAmountPaid || 0;
  const totalRegs = event.payoutInfo?.totalRegistrations ?? 0;
  const stats = event.payoutInfo?.paymentMethodsStats;
  const ws = event.payoutInfo?.withdrawalSummary;
  const withdrawals = event.payoutInfo?.withdrawals ?? [];
  const channels = ws?.settlementChannels;

  const liquidParish = getLiquidParish(event);
  const platformFee = totalAmountPaid - liquidParish;
  const alreadyWithdrawn = ws?.alreadyWithdrawn ?? 0;
  const availableBalance = getAvailableBalance(event);
  const withdrawnPct =
    liquidParish > 0 ? Math.min(100, (alreadyWithdrawn / liquidParish) * 100) : 0;

  const parseManualAmount = (raw: string): number => {
    const trimmed = raw.trim();
    if (!trimmed) return NaN;
    const normalized = trimmed.includes(',')
      ? trimmed.replace(/\./g, '').replace(',', '.')
      : trimmed;
    const value = Number(normalized);
    return Number.isFinite(value) ? value : NaN;
  };

  const handleRecordManualWithdrawal = async () => {
    const amount = parseManualAmount(manualAmount);
    if (!amount || amount <= 0) {
      onNotify?.('Informe um valor maior que zero.', 'error');
      return;
    }
    if (amount > availableBalance + 0.001) {
      onNotify?.(`O valor não pode ser maior que ${formatCurrency(availableBalance)}.`, 'error');
      return;
    }

    setRecording(true);
    try {
      const response = await withdrawalRequestService.markAsPaid(event._id, amount);
      onNotify?.(response.message || 'Saque registrado com sucesso.', 'success');
      setManualAmount('');
      await onWithdrawalRecorded?.();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || 'Não foi possível registrar o saque.';
      onNotify?.(message, 'error');
    } finally {
      setRecording(false);
    }
  };

  const maxCorrectionAmount =
    correctionTarget != null
      ? Math.max(0, liquidParish - (alreadyWithdrawn - correctionTarget.amount))
      : 0;

  const openCorrectionDialog = (withdrawal: { id: string; amount: number }) => {
    setCorrectionTarget({ id: withdrawal.id, amount: withdrawal.amount });
    setCorrectionAmount(withdrawal.amount.toFixed(2).replace('.', ','));
  };

  const resetCorrectionDialog = () => {
    setCorrectionTarget(null);
    setCorrectionAmount('');
  };

  const closeCorrectionDialog = () => {
    if (correcting) return;
    resetCorrectionDialog();
  };

  const handleCorrectManualWithdrawal = async () => {
    if (!correctionTarget) return;

    const amount = parseManualAmount(correctionAmount);
    if (!amount || amount <= 0) {
      onNotify?.('Informe um valor maior que zero.', 'error');
      return;
    }
    if (amount > maxCorrectionAmount + 0.001) {
      onNotify?.(`O valor não pode ser maior que ${formatCurrency(maxCorrectionAmount)}.`, 'error');
      return;
    }

    setCorrecting(true);
    try {
      const response = await withdrawalRequestService.correctManual(correctionTarget.id, amount);
      onNotify?.(response.message || 'Saque corrigido com sucesso.', 'success');
      resetCorrectionDialog();
      await onWithdrawalRecorded?.();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || 'Não foi possível corrigir o saque.';
      onNotify?.(message, 'error');
    } finally {
      setCorrecting(false);
    }
  };

  const handleDeleteManualWithdrawal = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const response = await withdrawalRequestService.deleteManual(deleteTarget.id);
      onNotify?.(response.message || 'Saque excluído com sucesso.', 'success');
      if (correctionTarget?.id === deleteTarget.id) {
        resetCorrectionDialog();
      }
      setDeleteTarget(null);
      await onWithdrawalRecorded?.();
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || 'Não foi possível excluir o saque.';
      onNotify?.(message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  React.useEffect(() => {
    if (availableBalance > 0) {
      setManualAmount(availableBalance.toFixed(2).replace('.', ','));
    } else {
      setManualAmount('');
    }
  }, [availableBalance, event._id]);

  const paymentMethodRows = stats
    ? (
        [
          { key: 'pix' as const, label: 'PIX' },
          { key: 'creditCard' as const, label: 'Cartão de crédito' },
          { key: 'debitCard' as const, label: 'Cartão de débito' },
          { key: 'other' as const, label: 'Outros / não classificado' },
        ] as const
      ).filter(({ key }) => (stats[key]?.count ?? 0) > 0)
    : [];

  const hasTickets =
    event.payoutInfo?.ticketBreakdown && event.payoutInfo.ticketBreakdown.length > 0;

  if (totalAmountPaid === 0) {
    return (
      <Box sx={{ py: 2, px: { xs: 1, sm: 2 }, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
        <Alert severity="info">Este evento ainda não possui inscrições pagas para repasse.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 2,
        px: { xs: 1.5, sm: 2.5 },
        bgcolor: (t) => (t.palette.mode === 'light' ? 'grey.50' : 'rgba(255,255,255,0.03)'),
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Contexto — uma linha só */}
      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
        <Chip size="small" label={event.parish?.name || 'Paróquia'} variant="outlined" />
        {(event.parish?.city || event.parish?.state) && (
          <Chip
            size="small"
            label={[event.parish?.city, event.parish?.state].filter(Boolean).join(', ')}
            variant="outlined"
          />
        )}
        <Chip
          size="small"
          label={`${formatDate(event.startDate)} – ${formatDate(event.endDate)}`}
          variant="outlined"
        />
        <Chip size="small" label={`Taxa ${feePercentage}%`} color="info" variant="outlined" />
        <Chip size="small" label={`${totalRegs} inscrição${totalRegs === 1 ? '' : 'ões'}`} variant="outlined" />
      </Stack>

      {/* 1 — Fluxo principal (único bloco de destaque) */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2,
          border: 2,
          borderColor: 'success.light',
          bgcolor: 'background.paper',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <AccountBalanceWallet color="success" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            Fluxo do dinheiro
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: { xs: 1, md: 0 },
          }}
        >
          <FlowStep label="Arrecadado" value={formatCurrency(totalAmountPaid)} sub="bruto" />
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', px: 0.5, color: 'text.disabled' }}>
            <SwapHoriz fontSize="small" />
          </Box>
          <FlowStep
            label={`Taxa plataforma (${feePercentage}%)`}
            value={`− ${formatCurrency(platformFee)}`}
            emphasis="deduct"
          />
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', px: 0.5, color: 'text.disabled' }}>
            <SwapHoriz fontSize="small" />
          </Box>
          <FlowStep label="Líquido paróquia" value={formatCurrency(liquidParish)} />
          {ws && (
            <>
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  px: 0.5,
                  color: 'text.disabled',
                }}
              >
                <SwapHoriz fontSize="small" />
              </Box>
              <FlowStep
                label="Já sacado"
                value={alreadyWithdrawn > 0 ? `− ${formatCurrency(alreadyWithdrawn)}` : formatCurrency(0)}
                emphasis={alreadyWithdrawn > 0 ? 'deduct' : 'neutral'}
              />
            </>
          )}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', px: 0.5, color: 'text.disabled' }}>
            <SwapHoriz fontSize="small" />
          </Box>
          <FlowStep
            label="Falta sacar"
            value={formatCurrency(availableBalance)}
            emphasis="hero"
            sub={ws?.pendingWithdrawalAmount ? 'ver saque em andamento abaixo' : undefined}
          />
        </Box>

        {ws && liquidParish > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Progresso de saque do líquido
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {withdrawnPct.toFixed(0)}% sacado
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={withdrawnPct}
              sx={{ height: 8, borderRadius: 4 }}
            />
            {ws.pendingWithdrawalAmount > 0 && (
              <Chip
                size="small"
                color="info"
                label={`Saque em andamento: ${formatCurrency(ws.pendingWithdrawalAmount)}`}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        )}

        {ws && availableBalance > 0 && ws.pendingWithdrawalAmount <= 0 && (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: (t) => (t.palette.mode === 'light' ? 'grey.50' : 'action.hover'),
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Registrar saque manual para a paróquia
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Informe o valor já repassado à paróquia (PIX, transferência ou outro meio fora do fluxo automático).
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            >
              <TextField
                label="Valor sacado"
                size="small"
                value={manualAmount}
                onChange={(e) => setManualAmount(e.target.value)}
                placeholder="0,00"
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                helperText={`Saldo disponível: ${formatCurrency(availableBalance)}`}
                sx={{ minWidth: { sm: 220 }, flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  setManualAmount(availableBalance.toFixed(2).replace('.', ','))
                }
                disabled={recording}
              >
                Usar saldo total
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={recording || !manualAmount.trim()}
                onClick={() => void handleRecordManualWithdrawal()}
                sx={{ minWidth: { sm: 160 } }}
              >
                {recording ? <CircularProgress size={20} color="inherit" /> : 'Registrar saque'}
              </Button>
            </Stack>
          </Paper>
        )}

        {ws && ws.pendingWithdrawalAmount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Há um saque em andamento para este evento. Conclua ou rejeite antes de registrar um saque manual.
          </Alert>
        )}

        {ws && availableBalance <= 0 && alreadyWithdrawn > 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Líquido da paróquia já foi totalmente registrado como sacado. Para ajustar um valor errado,
            use <strong>Corrigir</strong> no histórico de saques manuais abaixo.
          </Alert>
        )}
      </Paper>

      {/* 2 — Provedores (acordeão, aberto por padrão) */}
      {ws && channels && (
        <Accordion defaultExpanded disableGutters sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Onde está o dinheiro (por provedor)</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Valores contábeis deste evento. O saldo Pagar.me é da conta da paróquia (todos os eventos).
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provedor</TableCell>
                  <TableCell align="right">Líquido no evento</TableCell>
                  <TableCell align="right">Disponível / conta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip size="small" label="Pagar.me" sx={{ bgcolor: '#65A30022', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(channels.pagarmeParishNet)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatPagarmeBalance(
                      ws.pagarmeRecipientBalanceReais,
                      ws.pagarmeRecipientBalanceCode
                    )}
                    <Typography variant="caption" color="text.secondary" display="block">
                      conta recebedora
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip size="small" label="Mercado Pago · cartão" sx={{ bgcolor: '#009EE322', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(channels.mercadopagoCardParishNet)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(ws.mercadopagoCardAvailableReais ?? 0)}
                    <Typography variant="caption" color="text.secondary" display="block">
                      estim. não sacado
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip size="small" label="Mercado Pago · Pix" sx={{ bgcolor: '#009EE322', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(channels.mercadopagoPixParishNet)}</TableCell>
                  <TableCell align="right" color="text.secondary">
                    —
                  </TableCell>
                </TableRow>
                {(channels.mercadopagoAmbiguousParishNet ?? 0) > 0 && (
                  <TableRow>
                    <TableCell>
                      <Chip size="small" label="MP · método indefinido" color="warning" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(channels.mercadopagoAmbiguousParishNet)}</TableCell>
                    <TableCell align="right" color="text.secondary">
                      conferir extrato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      {/* 3 — Histórico de saques */}
      <Accordion
        defaultExpanded={withdrawals.length > 0}
        disableGutters
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>
            Histórico de saques
            {withdrawals.length > 0 && (
              <Chip size="small" label={withdrawals.length} sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {withdrawals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum saque solicitado ainda.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell align="right">Pagar.me</TableCell>
                  <TableCell align="right">Mercado Pago</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawals.map((w) => {
                  const st = WITHDRAWAL_STATUS[w.status] ?? {
                    label: w.status,
                    color: 'default' as const,
                  };
                  const isManual = w.source === 'admin_manual';
                  return (
                    <TableRow key={w.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {w.requestedAt
                            ? new Date(w.requestedAt).toLocaleString('pt-BR')
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(w.amount)}
                      </TableCell>
                      <TableCell>
                        <Chip label={st.label} size="small" color={st.color} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {w.source === 'admin_manual' ? (
                          <Chip label="Manual" size="small" color="info" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Paróquia
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {w.payoutBreakdown?.pagarmeAmountReais != null
                          ? formatCurrency(w.payoutBreakdown.pagarmeAmountReais)
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {w.payoutBreakdown?.mercadopagoAmountReais != null
                          ? formatCurrency(w.payoutBreakdown.mercadopagoAmountReais)
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {isManual && w.status === 'completed' ? (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Corrigir valor do saque manual">
                              <IconButton
                                size="small"
                                onClick={() => openCorrectionDialog({ id: w.id, amount: w.amount })}
                                aria-label="Corrigir saque manual"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir saque manual">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget({ id: w.id, amount: w.amount })}
                                aria-label="Excluir saque manual"
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={!!correctionTarget} onClose={closeCorrectionDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Corrigir saque manual</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ajuste o valor registrado. O saldo disponível do evento será recalculado automaticamente.
          </Typography>
          {correctionTarget && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Valor atual: <strong>{formatCurrency(correctionTarget.amount)}</strong>
            </Typography>
          )}
          <TextField
            fullWidth
            autoFocus
            label="Novo valor sacado"
            size="small"
            value={correctionAmount}
            onChange={(e) => setCorrectionAmount(e.target.value)}
            placeholder="0,00"
            disabled={correcting}
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
            helperText={`Máximo permitido: ${formatCurrency(maxCorrectionAmount)}`}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            color="error"
            onClick={() => {
              if (!correctionTarget) return;
              setDeleteTarget({ id: correctionTarget.id, amount: correctionTarget.amount });
            }}
            disabled={correcting || !correctionTarget}
          >
            Excluir saque
          </Button>
          <Box>
            <Button onClick={closeCorrectionDialog} disabled={correcting} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleCorrectManualWithdrawal()}
              disabled={correcting || !correctionAmount.trim()}
            >
              {correcting ? <CircularProgress size={20} color="inherit" /> : 'Salvar correção'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Excluir saque manual</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            O registro será removido e o valor voltará ao saldo disponível do evento.
          </Typography>
          {deleteTarget && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Valor a excluir: <strong>{formatCurrency(deleteTarget.amount)}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDeleteManualWithdrawal()}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Excluir saque'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 4 — Detalhes analíticos (fechados por padrão) */}
      {paymentMethodRows.length > 0 && (
        <Accordion disableGutters sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Receipt fontSize="small" color="action" />
              <Typography fontWeight={600}>Análise por método de pagamento</Typography>
              <Typography variant="caption" color="text.secondary">
                (estimativa com taxas do MP)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Método</TableCell>
                  <TableCell align="right">Transações</TableCell>
                  <TableCell align="right">Arrecadado</TableCell>
                  <TableCell align="right">Lucro estimado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentMethodRows.map(({ key, label }) => {
                  const m = stats![key];
                  return (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell align="right">{m?.count ?? 0}</TableCell>
                      <TableCell align="right">{formatCurrency(m?.totalAmount ?? 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(m?.totalProfit ?? 0)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      {hasTickets && (
        <Accordion disableGutters sx={{ ...accordionSx, mb: 0 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Por tipo de ingresso</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ingresso</TableCell>
                  <TableCell align="right">Qtd.</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Repasse</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {event.payoutInfo!.ticketBreakdown!.map((ticket, index) => (
                  <TableRow key={ticket._id || index}>
                    <TableCell>Ingresso {index + 1}</TableCell>
                    <TableCell align="right">{ticket.count}</TableCell>
                    <TableCell align="right">{formatCurrency(ticket.totalAmount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatCurrency(ticket.totalAmount * (1 - feePercentage / 100))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

type PayoutBrandScope = 'platform' | 'zuadaria';

const Payouts: React.FC<{ brandScope?: PayoutBrandScope }> = ({ brandScope = 'platform' }) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<PayoutFilters>({
    dateFilter: 'all',
    startDate: '',
    endDate: '',
    search: '',
    withSales: 'all',
    minAmount: '',
    status: 'all',
    payoutStatus: 'all',
  });
  const [searchInput, setSearchInput] = useState('');
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(true);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [detailsCache, setDetailsCache] = useState<Record<string, EventPayout>>({});
  const [loadingDetailsId, setLoadingDetailsId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const { data: eventsData, isLoading, error, isFetching } = useQuery({
    queryKey: ['payouts', brandScope, filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        dateFilter: filters.dateFilter || 'all',
        page: 1,
        limit: 500,
        sortBy: 'startDate',
        sortOrder: 'desc',
        includePayoutSummary: 1,
        brandScope,
      };
      if (filters.search) params.search = filters.search;
      if (filters.parishId) params.parishId = filters.parishId;
      if (filters.withSales && filters.withSales !== 'all') params.withSales = filters.withSales;
      if (filters.minAmount) params.minAmount = filters.minAmount;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.payoutStatus && filters.payoutStatus !== 'all') {
        params.payoutStatus = filters.payoutStatus;
      }

      const response = await api.get('/admin/events', { params });
      return response.data;
    },
  });

  const events: EventPayout[] = eventsData?.data?.events || [];
  const searchModeUsed: 'strict' | 'loose' | undefined = eventsData?.data?.searchMode;
  const backendSummary: PayoutDashboardSummary | undefined = eventsData?.data?.payoutSummary;

  const {
    data: mercadoPagoBalance,
    isLoading: mercadoPagoBalanceLoading,
    isError: mercadoPagoBalanceError,
    error: mercadoPagoBalanceQueryError,
  } = useQuery({
    queryKey: ['mercadopago-balance'],
    queryFn: async () => {
      const response = await withdrawalRequestService.getMercadoPagoBalance();
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Não foi possível consultar o saldo do Mercado Pago');
      }
      return response.data;
    },
    enabled: dashboardOpen && brandScope !== 'zuadaria',
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const mercadoPagoBalanceSub = useMemo(() => {
    if (mercadoPagoBalanceLoading) return 'Consultando API do Mercado Pago…';
    if (mercadoPagoBalanceError) {
      return getQueryErrorMessage(mercadoPagoBalanceQueryError) || 'Erro ao consultar saldo';
    }
    if (!mercadoPagoBalance) return 'Vendas Mercado Pago no sistema';
    if (mercadoPagoBalance.source === 'internal_estimate') {
      const pending =
        mercadoPagoBalance.unavailableBalance > 0
          ? ` · Pendente repasse: ${formatCurrency(mercadoPagoBalance.unavailableBalance)}`
          : '';
      return `Líquido MP nos eventos (estimativa)${pending}`;
    }
    if (mercadoPagoBalance.unavailableBalance > 0) {
      return `Pendente: ${formatCurrency(mercadoPagoBalance.unavailableBalance)} · Total: ${formatCurrency(mercadoPagoBalance.totalAmount)}`;
    }
    return `Total na conta: ${formatCurrency(mercadoPagoBalance.totalAmount)}`;
  }, [mercadoPagoBalance, mercadoPagoBalanceError, mercadoPagoBalanceLoading, mercadoPagoBalanceQueryError]);

  const dashboardStats = useMemo((): PayoutDashboardSummary => {
    if (backendSummary) {
      return {
        ...backendSummary,
        totalMercadopagoRemaining: backendSummary.totalMercadopagoRemaining ?? 0,
        totalPagarmeParishNet: backendSummary.totalPagarmeParishNet ?? 0,
        totalPagarmeRemaining: backendSummary.totalPagarmeRemaining ?? 0,
        totalPagarmeWalletAvailable: backendSummary.totalPagarmeWalletAvailable ?? null,
        totalPagarmeWaitingFunds: backendSummary.totalPagarmeWaitingFunds ?? 0,
        totalPagarmeWalletTransferred: backendSummary.totalPagarmeWalletTransferred ?? 0,
        totalPagarmeTransferable: backendSummary.totalPagarmeTransferable ?? null,
      };
    }

    const eventsWithSales = events.filter((e) => (e.payoutInfo?.totalRegistrations ?? 0) > 0);
    let totalGross = 0;
    let totalLiquid = 0;
    let totalWithdrawn = 0;
    let totalRemaining = 0;
    let totalPlatformFee = 0;
    let totalPendingAmount = 0;
    let countComplete = 0;
    let countNotPaid = 0;
    let countPartial = 0;
    let countPending = 0;
    let totalRegistrations = 0;

    for (const event of events) {
      const payoutInfo = event.payoutInfo || ({} as EventPayout['payoutInfo']);
      const gross = Number(payoutInfo.totalAmountPaid || 0);
      const liquid = getLiquidParish(event);
      const ws = payoutInfo.withdrawalSummary;
      const withdrawn = ws?.alreadyWithdrawn ?? 0;
      const remaining = getAvailableBalance(event);
      const pending = ws?.pendingWithdrawalAmount ?? 0;
      const status = getEventPayoutStatus(event);

      totalGross += gross;
      totalLiquid += liquid;
      totalWithdrawn += withdrawn;
      totalRemaining += remaining;
      totalPlatformFee += Number(payoutInfo.totalPlatformFee || 0);
      totalPendingAmount += pending;
      totalRegistrations += Number(payoutInfo.totalRegistrations || 0);

      if (status === 'complete') countComplete += 1;
      if (status === 'not_paid') countNotPaid += 1;
      if (status === 'partial') countPartial += 1;
      if (status === 'pending') countPending += 1;
    }

    return {
      eventCount: events.length,
      eventsWithSalesCount: eventsWithSales.length,
      totalGross,
      totalLiquid,
      totalWithdrawn,
      totalRemaining,
      totalPlatformFee,
      totalPendingAmount,
      totalRegistrations,
      countComplete,
      countNotPaid,
      countPartial,
      countPending,
      countNoSales: events.length - eventsWithSales.length,
      totalMercadopagoRemaining: 0,
      totalPagarmeParishNet: 0,
      totalPagarmeRemaining: 0,
      totalPagarmeWalletAvailable: null,
      totalPagarmeWaitingFunds: 0,
      totalPagarmeWalletTransferred: 0,
      totalPagarmeTransferable: null,
    };
  }, [backendSummary, events]);

  const pagarmePayoutCard = useMemo(() => {
    const ledger = dashboardStats.totalPagarmeRemaining ?? 0;
    const wallet = dashboardStats.totalPagarmeWalletAvailable;
    const transferable = dashboardStats.totalPagarmeTransferable;
    const waiting = dashboardStats.totalPagarmeWaitingFunds ?? 0;
    const transferred = dashboardStats.totalPagarmeWalletTransferred ?? 0;

    if (wallet == null || transferable == null) {
      return {
        value: ledger,
        sub: `Ainda não sacado · vendido ${formatCurrency(dashboardStats.totalPagarmeParishNet ?? 0)} (contábil; carteira Pagar.me indisponível)`,
      };
    }

    const gap = Math.max(0, parseFloat((ledger - wallet).toFixed(2)));
    const waitingPart = waiting > 0 ? ` · A liberar: ${formatCurrency(waiting)}` : '';
    const transferredPart =
      transferred > 0 ? ` · Já transferido na carteira: ${formatCurrency(transferred)}` : '';
    const gapPart =
      gap > 0.01
        ? ` · Contábil ${formatCurrency(ledger)} (diferença ${formatCurrency(gap)} sem caixa)`
        : ` · Contábil ${formatCurrency(ledger)}`;

    return {
      value: transferable,
      sub: `Em caixa agora: ${formatCurrency(wallet)}${waitingPart}${gapPart}${transferredPart}`,
    };
  }, [dashboardStats]);

  const handleFilterChange = (key: keyof PayoutFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.search?.trim()) count += 1;
    if (filters.dateFilter && filters.dateFilter !== 'all') count += 1;
    if (filters.startDate) count += 1;
    if (filters.endDate) count += 1;
    if (filters.payoutStatus && filters.payoutStatus !== 'all') count += 1;
    if (filters.withSales && filters.withSales !== 'all') count += 1;
    if (filters.minAmount) count += 1;
    if (filters.status && filters.status !== 'all') count += 1;
    return count;
  }, [filters]);

  const clearAllFilters = () => {
    setSearchInput('');
    setFilters({
      dateFilter: 'all',
      startDate: '',
      endDate: '',
      search: '',
      withSales: 'all',
      minAmount: '',
      status: 'all',
      payoutStatus: 'all',
    });
    setFiltersPanelOpen(false);
  };

  const periodLabel = useMemo(() => {
    if (filters.startDate && filters.endDate) {
      return `${filters.startDate.split('-').reverse().join('/')} – ${filters.endDate.split('-').reverse().join('/')}`;
    }
    if (filters.dateFilter === 'past') return 'Eventos passados';
    if (filters.dateFilter === 'upcoming') return 'Eventos futuros';
    if (filters.dateFilter === 'active') return 'Eventos ativos';
    return 'Todos os períodos';
  }, [filters.dateFilter, filters.startDate, filters.endDate]);

  const reloadEventPayoutDetails = useCallback(async (eventId: string) => {
    const response = await api.get(`/admin/events/${eventId}/payout`);
    if (response.data.success) {
      const detailed: EventPayout = {
        _id: response.data.data.event._id,
        title: response.data.data.event.title,
        startDate: response.data.data.event.startDate,
        endDate: response.data.data.event.endDate,
        feePercentage: response.data.data.event.feePercentage,
        parish: response.data.data.event.parish,
        payoutInfo: {
          ...response.data.data.payoutInfo,
          paymentMethodsStats: response.data.data.payoutInfo.paymentMethodsStats,
          withdrawalSummary: response.data.data.payoutInfo.withdrawalSummary,
          withdrawals: response.data.data.payoutInfo.withdrawals,
        },
      };
      setDetailsCache((prev) => ({ ...prev, [eventId]: detailed }));
    }
    await queryClient.invalidateQueries({ queryKey: ['payouts'] });
  }, [queryClient]);

  const toggleRow = useCallback(
    async (event: EventPayout) => {
      if (expandedEventId === event._id) {
        setExpandedEventId(null);
        return;
      }
      setExpandedEventId(event._id);
      if (detailsCache[event._id]) return;

      setLoadingDetailsId(event._id);
      try {
        const response = await api.get(`/admin/events/${event._id}/payout`);
        if (response.data.success) {
          const detailed: EventPayout = {
            _id: response.data.data.event._id,
            title: response.data.data.event.title,
            startDate: response.data.data.event.startDate,
            endDate: response.data.data.event.endDate,
            feePercentage: response.data.data.event.feePercentage,
            parish: response.data.data.event.parish,
            payoutInfo: {
              ...response.data.data.payoutInfo,
              paymentMethodsStats: response.data.data.payoutInfo.paymentMethodsStats,
              withdrawalSummary: response.data.data.payoutInfo.withdrawalSummary,
              withdrawals: response.data.data.payoutInfo.withdrawals,
            },
          };
          setDetailsCache((prev) => ({ ...prev, [event._id]: detailed }));
        }
      } catch {
        setSnackbar({ open: true, message: 'Erro ao carregar detalhes do repasse', severity: 'error' });
      } finally {
        setLoadingDetailsId(null);
      }
    },
    [expandedEventId, detailsCache]
  );

  const formatDate = (date: string | Date | undefined): string => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR');
  };

  const loadErrorMessage = error ? getQueryErrorMessage(error) : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        <Chip
          size="small"
          label={`${dashboardStats.eventCount} evento(s)`}
          sx={{ bgcolor: 'grey.100', fontWeight: 600 }}
        />
        <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
          <CalendarMonth sx={{ fontSize: 16 }} />
          <Typography variant="body2">{periodLabel}</Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        <TextField
          size="small"
          placeholder={
            brandScope === 'zuadaria'
              ? 'Buscar show, casa ou cidade…'
              : 'Buscar evento, paróquia ou cidade…'
          }
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: '1 1 240px', minWidth: 200, maxWidth: 520 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: isFetching && searchInput ? <CircularProgress size={16} /> : undefined,
          }}
        />
        <Button
          size="small"
          variant={filtersPanelOpen ? 'contained' : 'outlined'}
          startIcon={<Tune fontSize="small" />}
          onClick={() => setFiltersPanelOpen((v) => !v)}
        >
          Filtros
          {activeFiltersCount > 0 && (
            <Chip
              label={activeFiltersCount}
              size="small"
              color="primary"
              sx={{
                ml: 0.75,
                height: 20,
                minWidth: 20,
                ...(filtersPanelOpen ? { bgcolor: 'common.white', color: 'primary.main' } : {}),
              }}
            />
          )}
        </Button>
        {activeFiltersCount > 0 && (
          <Button size="small" color="inherit" startIcon={<Close fontSize="small" />} onClick={clearAllFilters}>
            Limpar
          </Button>
        )}
        <Button
          size="small"
          variant="text"
          onClick={() => setDashboardOpen((v) => !v)}
          endIcon={
            <ExpandMore
              sx={{
                transform: dashboardOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: '0.2s',
              }}
            />
          }
        >
          {dashboardOpen ? 'Ocultar resumo' : 'Ver resumo'}
        </Button>
      </Stack>

      <Collapse in={dashboardOpen}>
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryStatCard
            label="Falta repassar"
            value={formatCurrency(dashboardStats.totalRemaining)}
            sub={`${dashboardStats.countNotPaid + dashboardStats.countPartial} evento(s) com saldo`}
            color="success.main"
            icon={<AccountBalanceWallet fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryStatCard
            label="Falta pagar Mercado Pago"
            value={formatCurrency(dashboardStats.totalMercadopagoRemaining ?? 0)}
            sub="Líquido MP (já com 6,9% descontados) ainda não sacado"
            color="info.main"
            icon={<Payments fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryStatCard
            label="Pode sacar no Pagar.me"
            value={formatCurrency(pagarmePayoutCard.value)}
            sub={pagarmePayoutCard.sub}
            color="#65A300"
            icon={<SwapHoriz fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryStatCard
            label="Já repassado"
            value={formatCurrency(dashboardStats.totalWithdrawn)}
            sub={`${dashboardStats.countComplete} quitado(s)`}
            color="primary.main"
            icon={<CheckCircleOutline fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryStatCard
            label="Líquido paróquia"
            value={formatCurrency(dashboardStats.totalLiquid)}
            sub={`${dashboardStats.eventsWithSalesCount} com vendas · ${dashboardStats.totalRegistrations} inscrições`}
            icon={<ShoppingCart fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryStatCard
            label="Total arrecadado"
            value={formatCurrency(dashboardStats.totalGross)}
            sub={`Taxa plataforma: ${formatCurrency(dashboardStats.totalPlatformFee)}`}
            icon={<Receipt fontSize="small" />}
          />
        </Grid>
        {brandScope !== 'zuadaria' && (
        <Grid item xs={12} sm={6} md={3}>
          <SummaryStatCard
            label={
              mercadoPagoBalance?.source === 'internal_estimate'
                ? 'MP no sistema'
                : 'Saldo Mercado Pago'
            }
            value={
              mercadoPagoBalanceLoading
                ? '…'
                : mercadoPagoBalanceError
                  ? '—'
                  : formatCurrency(mercadoPagoBalance?.availableBalance ?? 0)
            }
            sub={mercadoPagoBalanceSub}
            color={
              mercadoPagoBalanceError
                ? 'error.main'
                : mercadoPagoBalance?.source === 'internal_estimate'
                  ? 'warning.main'
                  : 'info.main'
            }
            icon={
              mercadoPagoBalanceLoading ? (
                <CircularProgress size={18} />
              ) : (
                <Payments fontSize="small" />
              )
            }
          />
        </Grid>
        )}
        <Grid item xs={6} sm={4} md={2}>
          <SummaryStatCard
            label="Nada repassado"
            value={String(dashboardStats.countNotPaid)}
            sub="com vendas"
            color="warning.main"
            icon={<HourglassEmpty fontSize="small" />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryStatCard
            label="Parcial"
            value={String(dashboardStats.countPartial)}
            sub="incompleto"
            color="info.main"
            icon={<PendingActions fontSize="small" />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryStatCard
            label="Quitados"
            value={String(dashboardStats.countComplete)}
            sub="repasse completo"
            color="success.main"
            icon={<CheckCircleOutline fontSize="small" />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryStatCard
            label="Em andamento"
            value={String(dashboardStats.countPending)}
            sub={
              dashboardStats.totalPendingAmount > 0
                ? formatCurrency(dashboardStats.totalPendingAmount)
                : 'saques pendentes'
            }
            color="info.main"
            icon={<SwapHoriz fontSize="small" />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryStatCard
            label="Eventos"
            value={String(dashboardStats.eventCount)}
            sub="no filtro"
            icon={<EventAvailable fontSize="small" />}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryStatCard
            label="Sem vendas"
            value={String(dashboardStats.countNoSales)}
            sub="sem inscrições pagas"
          />
        </Grid>
      </Grid>
      </Collapse>

      {!dashboardOpen && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Chip size="small" variant="outlined" label={`Falta: ${formatCurrency(dashboardStats.totalRemaining)}`} color="success" />
          <Chip size="small" variant="outlined" label={`Sacado: ${formatCurrency(dashboardStats.totalWithdrawn)}`} />
          <Chip size="small" variant="outlined" label={`Arrecadado: ${formatCurrency(dashboardStats.totalGross)}`} />
        </Stack>
      )}

      {searchModeUsed === 'loose' && filters.search?.trim() && (
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }} icon={false}>
          <Typography variant="body2">
            Busca aproximada para &quot;{filters.search}&quot;.
          </Typography>
        </Alert>
      )}

      <Collapse in={filtersPanelOpen}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Repasse</InputLabel>
              <Select
                label="Repasse"
                value={filters.payoutStatus || 'all'}
                onChange={(e) => handleFilterChange('payoutStatus', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="not_paid">Nada repassado</MenuItem>
                <MenuItem value="partial">Parcial</MenuItem>
                <MenuItem value="complete">Quitado</MenuItem>
                <MenuItem value="pending">Em andamento</MenuItem>
                <MenuItem value="no_sales">Sem vendas</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Período</InputLabel>
              <Select
                label="Período"
                value={filters.dateFilter || 'all'}
                onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Ativos</MenuItem>
                <MenuItem value="upcoming">Futuros</MenuItem>
                <MenuItem value="past">Passados</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="De"
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <TextField
              size="small"
              label="Até"
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Vendas</InputLabel>
              <Select
                label="Vendas"
                value={filters.withSales || 'all'}
                onChange={(e) => handleFilterChange('withSales', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="withSales">Com vendas</MenuItem>
                <MenuItem value="withoutSales">Sem vendas</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Mín. arrecadado"
              type="number"
              value={filters.minAmount || ''}
              onChange={(e) => handleFilterChange('minAmount', e.target.value)}
              sx={{ width: 140 }}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Evento</InputLabel>
              <Select
                label="Evento"
                value={filters.status || 'all'}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="published">Publicados</MenuItem>
                <MenuItem value="draft">Rascunhos</MenuItem>
                <MenuItem value="paused">Pausados</MenuItem>
                <MenuItem value="cancelled">Cancelados</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      </Collapse>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', flex: 1 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">
                Erro ao carregar repasses{loadErrorMessage ? `: ${loadErrorMessage}` : ''}
              </Alert>
            </Box>
          ) : events.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="info">Nenhum evento encontrado com os filtros selecionados</Alert>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
              <Box sx={{ minWidth: 640 }}>
              <Box
                sx={[
                  {
                    display: 'grid',
                    gridTemplateColumns: PAYOUT_ROW_GRID,
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  },
                  payoutHeaderCellSx,
                ]}
              >
                <Box />
                <Box sx={{ px: 0 }}>Evento / Descrição</Box>
                <Box sx={{ textAlign: 'right', px: 0 }}>Arrecadado</Box>
                <Box sx={{ textAlign: 'right', px: 0 }}>Sacado</Box>
                <Box sx={{ textAlign: 'right', px: 0 }}>Falta sacar</Box>
              </Box>

              {events.map((event) => {
                const payoutInfo = event.payoutInfo || ({} as EventPayout['payoutInfo']);
                const totalAmountPaid = Number(payoutInfo.totalAmountPaid) || 0;
                const liquidParish = getLiquidParish(event);
                const ws = payoutInfo.withdrawalSummary;
                const alreadyWithdrawn = ws?.alreadyWithdrawn ?? 0;
                const availableBalance = getAvailableBalance(event);
                const hasPayout = liquidParish > 0;
                const parish =
                  (event as { parish?: { name?: string; city?: string; state?: string } }).parish ||
                  null;
                const parishName = parish?.name || 'N/A';
                const isExpanded = expandedEventId === event._id;
                const isLoadingDetails = loadingDetailsId === event._id;
                const detailEvent = detailsCache[event._id] ?? event;
                const subtitle = formatEventSubtitle(
                  parishName,
                  event.startDate,
                  event.endDate,
                  formatDate
                );

                return (
                  <Box key={event._id} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Box
                      onClick={() => toggleRow(event)}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: PAYOUT_ROW_GRID,
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1.5,
                        cursor: 'pointer',
                        bgcolor: hasPayout ? 'rgba(76, 175, 80, 0.03)' : 'transparent',
                        ...(isExpanded && { bgcolor: 'action.selected' }),
                        '&:hover': { bgcolor: 'action.hover' },
                        '& .expand-toggle': {
                          transition: 'transform 0.2s ease',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        className="expand-toggle"
                        onClick={(e) => {
                          e.stopPropagation();
                          void toggleRow(event);
                        }}
                        sx={{ color: isExpanded ? 'primary.main' : 'text.secondary' }}
                      >
                        <KeyboardArrowDown fontSize="small" />
                      </IconButton>

                      <Box sx={{ minWidth: 0, pr: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap title={event.title}>
                          {event.title}
                        </Typography>
                        {subtitle && (
                          <Typography variant="body2" color="text.secondary" noWrap title={subtitle}>
                            {subtitle}
                          </Typography>
                        )}
                      </Box>

                      {totalAmountPaid > 0 ? (
                        <>
                          <Typography variant="body1" fontWeight={600} textAlign="right">
                            {formatCurrency(totalAmountPaid)}
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            textAlign="right"
                            color={alreadyWithdrawn > 0 ? 'text.primary' : 'text.secondary'}
                          >
                            {formatCurrency(alreadyWithdrawn)}
                          </Typography>
                          <Typography variant="body1" fontWeight={700} textAlign="right" color="primary.main">
                            {formatCurrency(availableBalance)}
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic"
                          sx={{ gridColumn: '3 / -1', textAlign: 'right' }}
                        >
                          Sem arrecadação registrada
                        </Typography>
                      )}
                    </Box>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {isLoadingDetails ? (
                              <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                gap={1.5}
                                py={4}
                                sx={{
                                  bgcolor: (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'grey.50'
                                      : 'rgba(255,255,255,0.04)',
                                }}
                              >
                                <CircularProgress size={28} />
                                <Typography variant="body2" color="text.secondary">
                                  Carregando detalhes…
                                </Typography>
                              </Box>
                            ) : (
                              <PayoutExpandPanel
                                event={detailEvent}
                                formatDate={formatDate}
                                onWithdrawalRecorded={() => reloadEventPayoutDetails(event._id)}
                                onNotify={(message, severity) =>
                                  setSnackbar({ open: true, message, severity })
                                }
                              />
                            )}
                    </Collapse>
                  </Box>
                );
              })}
              </Box>
            </Box>
          )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Payouts;

